import { useState } from "react";
import type { RepoConfig } from "../lib/github";
import { verifyAccess } from "../lib/github";

/**
 * First-run setup: paste a fine-grained PAT.
 *
 * The token is stored in localStorage on this device only. It never leaves the
 * browser except to call api.github.com directly.
 */
export function Setup({
  initial,
  onSave,
  onCancel,
  onDemo,
}: {
  initial?: RepoConfig | null;
  onSave: (cfg: RepoConfig) => void;
  onCancel?: () => void;
  onDemo?: () => void;
}) {
  const [owner, setOwner] = useState(initial?.owner ?? "");
  const [repo, setRepo] = useState(initial?.repo ?? "");
  const [branch, setBranch] = useState(initial?.branch ?? "main");
  const [token, setToken] = useState(initial?.token ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setBusy(true);
    setError(null);
    const cfg: RepoConfig = { owner: owner.trim(), repo: repo.trim(), branch: branch.trim(), token: token.trim() };
    try {
      const { defaultBranch } = await verifyAccess(cfg);
      onSave({ ...cfg, branch: cfg.branch || defaultBranch });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Connection failed");
    } finally {
      setBusy(false);
    }
  }

  const valid = owner.trim() && repo.trim() && token.trim();

  return (
    <div className="mx-auto max-w-lg p-5">
      <h1 className="mb-1 text-xl font-semibold text-ink-100">PsychoTp · Console</h1>
      <p className="mb-6 text-sm text-ink-400">
        Connect to the PsychoTp repo: answer decisions in the Deck, steer areas in
        Questions, browse items in Docs, watch Activity and your session log.
      </p>

      <div className="card space-y-4 p-4">
        <Field label="Owner" value={owner} onChange={setOwner} placeholder="your-github-user" />
        <Field label="Repository" value={repo} onChange={setRepo} placeholder="psychotp" />
        <Field label="Branch" value={branch} onChange={setBranch} placeholder="main" />
        <Field
          label="Fine-grained PAT"
          value={token}
          onChange={setToken}
          placeholder="github_pat_..."
          type="password"
          hint="Needs Contents: Read and write. Add Actions: Read and write for the Agents panel (run status, steering, dispatch)."
        />

        {error && (
          <div className="rounded-xl border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
            {error}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button className="btn-primary flex-1" disabled={!valid || busy} onClick={handleSave}>
            {busy ? "Checking…" : "Connect"}
          </button>
          {onCancel && (
            <button className="btn-ghost" onClick={onCancel} disabled={busy}>
              Cancel
            </button>
          )}
        </div>

        {onDemo && (
          <button
            onClick={onDemo}
            className="w-full rounded-xl border border-dashed border-ink-700 px-3 py-2.5
                       text-sm text-ink-400 hover:border-accent hover:text-accent-soft"
          >
            Preview with sample items (no token needed)
          </button>
        )}
      </div>

      <p className="mt-4 text-xs leading-relaxed text-ink-500">
        The token is stored only in this browser. Create one at GitHub → Settings →
        Developer settings → Fine-grained tokens, with repository access limited to
        this repo, <span className="font-mono">Contents: Read and write</span>, and
        <span className="font-mono"> Actions: Read and write</span> for the Agents panel
        (run status, enable/disable, dispatch).
      </p>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-ink-500">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        className="w-full rounded-xl border border-ink-700 bg-ink-950 px-3 py-2.5 text-[15px]
                   text-ink-100 placeholder:text-ink-600 focus:border-accent focus:outline-none"
      />
      {hint && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
    </label>
  );
}
